import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {STAGE_RUN_STATUS, STAGE_RUN_STATUS_VIRTUAL} from 'workflows/constants/StageRunConstants';
import {experimentalConditionValuesToString} from 'workflows/util/ExperimentalConditionUtil';
import {StageRunCircle} from 'workflows/visualization/StageRunVisualization';
import {EXPERIMENTAL_CONDITION_VALUE_SHAPE, ExperimentalConditionValue} from 'workflows/WorkflowConstants';

import {POSITION_SHAPE} from './GraphLayout';
import {STAGE_RUN_RADIUS} from './GraphLayoutConstants';
import {HighlightMode} from './StageFlowNodeHighlightUtil';

interface StageFlowNodeSecondaryLinkVisualizationProps {
  highlightMode: HighlightMode;
  isDiscarded: boolean;
  onClick?: (e: React.MouseEvent<SVGPathElement>) => void;
  leftPosition: {x: number; y: number};
  rightPosition: {x: number; y: number};
}

/**
 * Visualize a link between a child and a non-primary parent.
 */
export function StageFlowNodeSecondaryLinkVisualization(
  props: StageFlowNodeSecondaryLinkVisualizationProps
): JSX.Element {
  const {highlightMode, isDiscarded, onClick, leftPosition, rightPosition} = props;
  const d = `
    M ${leftPosition.x} ${leftPosition.y}
    L ${rightPosition.x} ${rightPosition.y}
  `;

  return (
    <g>
      <path
        className={classNames('stageFlowNodeSecondaryLinkVisualization', {
          'stageRunLinkVisualization-line--discarded': isDiscarded,
          'is-default': highlightMode === HighlightMode.DEFAULT,
          'is-dehighlighted': highlightMode === HighlightMode.DEHIGHLIGHTED,
        })}
        d={d}
      />
      {onClick && <path className="stageRunLinkVisualization-clickTarget" d={d} onClick={onClick} />}
    </g>
  );
}

StageFlowNodeSecondaryLinkVisualization.propTypes = {
  highlightMode: PropTypes.oneOf(Object.values(HighlightMode)).isRequired,
  isDiscarded: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  leftPosition: POSITION_SHAPE,
  rightPosition: POSITION_SHAPE,
};

StageFlowNodeSecondaryLinkVisualization.defaultProps = {
  isDiscarded: false,
};

interface StageFlowNodeVisualizationProps {
  className?: string;
  experimentalConditionValues: Array<ExperimentalConditionValue>;
  highlightMode: HighlightMode;
  isHovered: boolean;
  // Determine whether the cursor should be a pointer.
  // The onClick handler is still called when the node is clicked, which can be useful e.g. if you want
  // to stop propagation.
  isNodeClickable: boolean;
  onClick?: (e: React.MouseEvent<SVGGElement>) => void;
  onHover?: () => void;
  onRightClick?: (e: React.MouseEvent<SVGGElement>) => void;
  onUnhover?: () => void;
  position: {x: number; y: number};
  relativeX: number;
  shouldShowExperimentalCondition: boolean;
  stageRunStatus?: STAGE_RUN_STATUS | typeof STAGE_RUN_STATUS_VIRTUAL | null;
  stageWidth: number;
  tooltipTitle?: string | null;
}

class StageFlowNodeVisualization extends React.Component<StageFlowNodeVisualizationProps> {
  static propTypes = {
    className: PropTypes.string,
    experimentalConditionValues: PropTypes.arrayOf(EXPERIMENTAL_CONDITION_VALUE_SHAPE.isRequired).isRequired,
    highlightMode: PropTypes.oneOf(Object.values(HighlightMode)).isRequired,
    isHovered: PropTypes.bool.isRequired,
    // Determine whether the cursor should be a pointer.
    // The onClick handler is still called when the node is clicked, which can be useful e.g. if you want
    // to stop propagation.
    isNodeClickable: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    onHover: PropTypes.func,
    onRightClick: PropTypes.func,
    onUnhover: PropTypes.func,
    position: POSITION_SHAPE.isRequired,
    relativeX: PropTypes.number.isRequired,
    shouldShowExperimentalCondition: PropTypes.bool.isRequired,
    stageRunStatus: PropTypes.oneOf([...Object.values(STAGE_RUN_STATUS), STAGE_RUN_STATUS_VIRTUAL]),
    stageWidth: PropTypes.number.isRequired,
    tooltipTitle: PropTypes.string,
  };

  static defaultProps = {
    isHovered: false,
  };

  _renderNode(): JSX.Element {
    const {
      isNodeClickable,
      onClick,
      onRightClick,
      onHover,
      onUnhover,
      position,
      className,
      highlightMode,
      isHovered,
      stageRunStatus,
    } = this.props;
    return (
      <g
        className={classNames({'u-pointer': isNodeClickable})}
        onClick={onClick}
        onContextMenu={onRightClick}
        onMouseEnter={onHover}
        onMouseLeave={onUnhover}
      >
        {/** Use background circle to cover up links underneath */}
        <circle
          className="stageRunVisualization-background"
          cx={position.x}
          cy={position.y}
          r={STAGE_RUN_RADIUS}
        />
        {/* Pass in INITIALIZED here instead of as a default prop to handle the case of stage flow nodes,
         * which have a null status. Ideally this prop would describe the display style
         * instead of the status.
         */}
        <StageRunCircle
          className={className}
          highlightMode={highlightMode}
          isHovered={isHovered}
          position={position}
          radius={STAGE_RUN_RADIUS}
          stageRunStatus={stageRunStatus || STAGE_RUN_STATUS.INITIALIZED}
        />
      </g>
    );
  }

  _renderTooltipNode(): JSX.Element {
    return <TooltipWrapper tooltipTitle={this.props.tooltipTitle}>{this._renderNode()}</TooltipWrapper>;
  }

  override render(): JSX.Element {
    const {shouldShowExperimentalCondition, experimentalConditionValues, position, stageWidth, relativeX} =
      this.props;
    return (
      <g>
        {this._renderTooltipNode()}
        {shouldShowExperimentalCondition && experimentalConditionValues.length > 0 && (
          <foreignObject
            x={position.x + STAGE_RUN_RADIUS + 5}
            y={position.y - 14}
            height={20}
            width={stageWidth - relativeX - 2 * STAGE_RUN_RADIUS}
          >
            <div className="stageFlowNodeVisualization-experimentalCondition u-ellipsis-text">
              {experimentalConditionValuesToString(experimentalConditionValues)}
            </div>
          </foreignObject>
        )}
      </g>
    );
  }
}

export default StageFlowNodeVisualization;
